import { ROLE_CONFIG, normalizeRole } from '../config/app.config';

export const ROLES = {
  ADMIN: ROLE_CONFIG.admin,
  INSTRUCTOR: ROLE_CONFIG.instructor,
  STUDENT: ROLE_CONFIG.student,
  TA: ROLE_CONFIG.ta,
} as const;

export type UserRole = typeof ROLES[keyof typeof ROLES];

export const isValidRole = (role: string): role is UserRole => {
  const normalized = normalizeRole(role);
  return Object.values(ROLES).includes(normalized as UserRole);
};

export const normalizeUserRole = (role: string): UserRole => {
  const normalized = normalizeRole(role);
  if (isValidRole(normalized)) {
    return normalized as UserRole;
  }
  return ROLES.STUDENT;
};

// Role hierarchy for permission checking (lower number = higher privilege)
export const ROLE_HIERARCHY: Record<UserRole, number> = {
  [ROLES.ADMIN]: 0,
  [ROLES.INSTRUCTOR]: 1,
  [ROLES.TA]: 2,
  [ROLES.STUDENT]: 3,
};

// Helper function to check if a role has permission (is equal or higher in hierarchy)
export const hasRolePermission = (
  userRole: UserRole,
  requiredRole: UserRole
): boolean => {
  const userLevel = ROLE_HIERARCHY[userRole];
  const requiredLevel = ROLE_HIERARCHY[requiredRole];
  return userLevel !== undefined && requiredLevel !== undefined && userLevel <= requiredLevel;
};

// Export individual role constants for convenience
export const {
  ADMIN,
  INSTRUCTOR,
  STUDENT,
  TA,
} = ROLES;
